import React, { useEffect, useState, useCallback } from 'react';
import { Shield, AlertTriangle, Activity, Ban, TrendingUp, Download } from 'lucide-react';
import apiClient from '../services/api';
import AttacksTimeline from '../components/AttacksTimeline';

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-slate-900" data-testid={`cyber-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</p>
      </div>
    </div>
  </div>
);

const heatColor = (count, max) => {
  const t = max ? count / max : 0;
  if (t > 0.8) return 'bg-red-600';
  if (t > 0.6) return 'bg-red-500';
  if (t > 0.4) return 'bg-amber-500';
  if (t > 0.2) return 'bg-amber-400';
  if (t > 0.05) return 'bg-emerald-500';
  return 'bg-slate-300';
};

const CybersecDashboard = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/security/cognitive-stats');
      setStats(data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // rafraichissement temps reel 5s
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="p-8 text-slate-500">Chargement...</div>;
  if (error) return (
    <div className="p-8 text-red-600" data-testid="cyber-error">
      {error === 'Admin only' ? 'Reserve aux administrateurs.' : error}
    </div>
  );

  const cog = stats?.cognitive || {};
  const rl = stats?.rate_limiter || {};
  const patterns = cog.top_patterns || [];
  const maxPat = patterns.reduce((m, p) => Math.max(m, p.count), 0);

  return (
    <div className="p-6 space-y-6" data-testid="cybersec-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-cyan-600" /> Securite Plateforme
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Firewall cognitif - actualisation temps reel (5s)
            {(cog.profiles_tracked === 0 && (cog.currently_blocked || []).length === 0) && (
              <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="cog-disabled-badge">
                DESACTIVE
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <button
            onClick={async () => {
              try {
                const r = await apiClient.get('/security/cognitive-stats/export.csv', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
                const a = document.createElement('a');
                a.href = url; a.download = `cybersec_${Date.now()}.csv`; a.click();
                window.URL.revokeObjectURL(url);
              } catch { /* ignore */ }
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
            data-testid="cyber-export-csv"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Activity} label="Profils suivis" value={cog.profiles_tracked || 0} color="bg-cyan-600" />
        <KPI icon={Ban} label="IPs bloquees" value={(cog.currently_blocked || []).length} color="bg-red-600" />
        <KPI icon={AlertTriangle} label="Auth failures" value={(rl.auth_failures || []).length} color="bg-amber-500" />
        <KPI icon={TrendingUp} label="Patterns detectes" value={patterns.length} color="bg-slate-800" />
      </div>

      <AttacksTimeline />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top patterns / heatmap */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Top 10 patterns (60 dernieres sec)</h3>
          {patterns.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune activite suspecte detectee.</p>
          ) : (
            <div className="space-y-2">
              {patterns.map((p) => (
                <div key={p.path} className="flex items-center gap-3">
                  <div className={`w-2 h-6 rounded ${heatColor(p.count, maxPat)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-slate-700 truncate" title={p.path}>{p.path}</p>
                  </div>
                  <span className="text-xs text-slate-500 tabular-nums w-8 text-right">{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* IPs bloquees */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">IPs actuellement bloquees</h3>
          {(cog.currently_blocked || []).length === 0 ? (
            <p className="text-sm text-slate-500">Aucun blocage actif.</p>
          ) : (
            <div className="space-y-2">
              {cog.currently_blocked.map((b) => (
                <div key={b.ip} className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-100">
                  <span className="font-mono text-xs text-slate-700">{b.ip}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-red-700 font-medium">TTL {Math.ceil(b.ttl_seconds/60)}min</span>
                    <span className="text-slate-500">recidive x{b.streak}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await apiClient.post(`/security/cognitive-stats/unblock/${b.ip}`);
                          load();
                        } catch (e) {
                          setError(e?.response?.data?.detail || 'Deblocage impossible');
                        }
                      }}
                      className="px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`unblock-ip-${b.ip}`}
                    >
                      Debloquer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Velocity top */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Top 10 IPs par velocity (req/60s)</h3>
        {(cog.velocity_top || []).length === 0 ? (
          <p className="text-sm text-slate-500">Pas de trafic significatif.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-100">
                <th className="text-left py-2">IP</th>
                <th className="text-right py-2">Req/60s</th>
                <th className="text-right py-2">Score</th>
              </tr>
            </thead>
            <tbody>
              {cog.velocity_top.map((v) => (
                <tr key={v.ip} className="border-b border-slate-50">
                  <td className="py-2 font-mono text-xs text-slate-700">{v.ip}</td>
                  <td className="py-2 text-right tabular-nums">{v.requests_60s}</td>
                  <td className="py-2 text-right">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      v.score > 40 ? 'bg-red-100 text-red-700' :
                      v.score > 15 ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-50 text-emerald-700'
                    }`}>
                      {v.score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-slate-400 text-center">
        Les IPs sont anonymisees par hash SHA-256 - conformite RGPD
      </p>
    </div>
  );
};

export default CybersecDashboard;
